/**
 * GeneratedDashboardPreview Component
 *
 * Displays a preview of the generated dashboard configuration
 * with validation status and apply action.
 */

import React, { useState } from 'react';
import { useAgent } from '../../contexts/AgentContext';
import './GeneratedDashboardPreview.css';

const GeneratedDashboardPreview = () => {
  const { generatedDashboard, validationResult, applyConfig, loading } = useAgent();
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);

  if (!generatedDashboard) {
    return null;
  }

  const handleApply = async () => {
    setApplying(true);
    try {
      await applyConfig(true); // append to existing
      setApplied(true);
      setTimeout(() => setApplied(false), 3000);
    } catch (err) {
      console.error('Failed to apply configuration:', err);
    } finally {
      setApplying(false);
    }
  };

  const { dashboard_id, dashboard_tab, template_id, title, sections } = generatedDashboard;
  const { is_valid, invalid_metrics, warnings } = validationResult;

  return (
    <div className="dashboard-preview">
      <div className="preview-header">
        <h3>Generated Dashboard</h3>
        <div className={`validation-badge ${is_valid ? 'valid' : 'invalid'}`}>
          {is_valid ? '✓ Valid' : '✗ Invalid'}
        </div>
      </div>

      <div className="preview-content">
        <div className="preview-metadata">
          <div className="metadata-item">
            <span className="metadata-label">Dashboard ID:</span>
            <span className="metadata-value">{dashboard_id}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Tab Name:</span>
            <span className="metadata-value">{dashboard_tab}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Template:</span>
            <span className="metadata-value">{template_id}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Title:</span>
            <span className="metadata-value">{title}</span>
          </div>
        </div>

        <div className="preview-sections">
          <h4>Charts ({sections?.length || 0})</h4>
          <div className="sections-list">
            {sections && sections.map((section, index) => (
              <div key={index} className="section-item">
                <div className="section-number">{index + 1}</div>
                <div className="section-details">
                  <div className="section-name">{section.name}</div>
                  <div className="section-meta">
                    <span className="chart-type">{section.type}</span>
                    <span className="metric-id">{section.metric_id}</span>
                    {section.second_metric_id && (
                      <span className="metric-id secondary">+ {section.second_metric_id}</span>
                    )}
                    <span className="interval">{section.interval_period}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {!is_valid && invalid_metrics && invalid_metrics.length > 0 && (
          <div className="validation-errors">
            <h4>Invalid Metrics</h4>
            <ul>
              {invalid_metrics.map((metric, index) => (
                <li key={index}>{metric}</li>
              ))}
            </ul>
          </div>
        )}

        {warnings && warnings.length > 0 && (
          <div className="validation-warnings">
            <h4>Warnings</h4>
            <ul>
              {warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="preview-actions">
        <button
          className="apply-button"
          onClick={handleApply}
          disabled={!is_valid || applying || loading || applied}
        >
          {applying ? 'Applying...' : applied ? 'Applied!' : 'Apply Dashboard'}
        </button>
        {!is_valid && (
          <p className="apply-disabled-message">
            Cannot apply dashboard with invalid metrics
          </p>
        )}
      </div>
    </div>
  );
};

export default GeneratedDashboardPreview;
